import { useRouter } from 'next/router'
import { Theme, MenuItem, Select, Typography, Button } from '@mui/material'
import { makeStyles, createStyles } from '@mui/styles'
import { memo, useState, useEffect, useContext } from 'react'
import { InputNames } from '../../../contexts/LoanSelectorContext'
import { convertToGermanLocale } from '../../../utils/banklist'
import {
  NUM_OF_VISIBLE_OPTIONS,
  PURPOSE_OPTIONS,
  TERM_OPTIONS,
  getAmountOptions,
} from '../../../utils/loanSelector.utils'
import { BankListContext } from '../BankListContext'
import { LoanSelectorStateType } from './typings'

const useStyles = makeStyles<Theme>((theme: Theme) =>
  createStyles({
    loanSelectorContainer: {
      margin: '30px auto',
      maxWidth: '1200px',
      padding: '22px 26px 0px 26px',
      [theme.breakpoints.down('md')]: {
        padding: 0,
        margin: '20px 10px',
      },
    },
    inputCls: {
      minWidth: '150px',
      [theme.breakpoints.down('md')]: {
        width: '100%',
        margin: '10px 0',
      },
    },
    buttonCls: {
      minWidth: '200px',
      [theme.breakpoints.down('lg')]: {
        width: '100%',
        margin: '10px 0',
      },
    },
    loanSelectorForm: {
      padding: 25,
      boxShadow: `${theme.palette.grey[300]} 0px 2px 20px 0px`,
      borderRadius: 10,
      display: 'grid',
      gridAutoFlow: 'column',
      gridTemplateColumns: '1fr 200px',
      alignItems: 'end',
      gap: 15,
      [theme.breakpoints.down('lg')]: {
        gridAutoFlow: 'row',
        gridTemplateColumns: '1fr',
      },
      [theme.breakpoints.down('md')]: {
        padding: 15,
      },
    },
    loanSelectorInput: {
      display: 'grid',
      gridAutoFlow: 'column',
      gridTemplateColumns: 'repeat(3, 1fr)',
      alignItems: 'center',
      gap: 15,
      [theme.breakpoints.down('md')]: {
        gridAutoFlow: 'row',
        gridTemplateColumns: '1fr',
        gap: 10,
      },
    },
  }),
)
const LoanSelector = () => {
  const router = useRouter()
  const minAmount = router?.query?.minAmount as unknown as number
  const maxAmount = router?.query?.maxAmount as unknown as number

  const {
    handleLoanSelectorValueChange: handleChange,
    loanSelectorState: defaultState,
  } = useContext(BankListContext)

  const [loanSelectorState, setLoanSelectorState] =
    useState<LoanSelectorStateType>()

  const {
    loanSelectorForm,
    loanSelectorContainer,
    loanSelectorInput,
    inputCls,
    buttonCls,
  } = useStyles()

  useEffect(() => {
    setLoanSelectorState({
      purpose: defaultState?.purpose as string,
      amount: defaultState?.amount,
      term: defaultState?.term,
    })
  }, [defaultState])

  useEffect(() => {
    handleChange?.(loanSelectorState as LoanSelectorStateType)
  }, [handleChange, loanSelectorState])

  return loanSelectorState ? (
    <div className={loanSelectorContainer}>
      <div className={loanSelectorForm}>
        <div className={loanSelectorInput}>
          <div className={inputCls}>
            <Typography>Verwendung</Typography>
            <Select
              fullWidth
              value={loanSelectorState[InputNames.Purpose]}
              onChange={(event) => {
                setLoanSelectorState({
                  ...loanSelectorState,
                  purpose: event.target.value,
                })
              }}
            >
              {PURPOSE_OPTIONS.map((purposeOption) => (
                <MenuItem
                  key={`${purposeOption.value}`.toString()}
                  value={`${purposeOption.value}`.toString()}
                >
                  {purposeOption.label}
                </MenuItem>
              ))}
            </Select>
          </div>
          <div className={inputCls}>
            <Typography id={InputNames.Amount}>Nettokreditbetrag</Typography>
            <Select
              fullWidth
              value={loanSelectorState[InputNames.Amount]}
              onChange={(event) => {
                setLoanSelectorState({
                  ...loanSelectorState,
                  amount: event.target.value as number,
                })
              }}
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: 40 * NUM_OF_VISIBLE_OPTIONS,
                  },
                },
              }}
            >
              {getAmountOptions(minAmount, maxAmount).map((amountOption) => (
                <MenuItem key={amountOption} value={amountOption}>
                  {convertToGermanLocale(amountOption)} €
                </MenuItem>
              ))}
            </Select>
          </div>
          <div className={inputCls}>
            <Typography>Kreditlaufzeit</Typography>
            <Select
              fullWidth
              value={loanSelectorState[InputNames.Term]}
              onChange={(event) => {
                setLoanSelectorState({
                  ...loanSelectorState,
                  term: event.target.value as number,
                })
              }}
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: 40 * NUM_OF_VISIBLE_OPTIONS,
                    margin: 0,
                  },
                },
              }}
            >
              {TERM_OPTIONS.map((termOption) => (
                <MenuItem key={termOption} value={termOption}>
                  {termOption} Monate
                </MenuItem>
              ))}
            </Select>
          </div>
        </div>
        <Button
          variant='contained'
          size='large'
          className={buttonCls}
          sx={{
            height: '55px',
          }}
          onClick={() => handleChange?.(loanSelectorState)}
        >
          Jetzt vergleichen
        </Button>
      </div>
    </div>
  ) : null
}

export default memo(LoanSelector)
